import { useEffect, useState, useCallback } from "react";
import izenuAPI from "../../services/izenuAPI";

/**
 * Saúde do registo de streams, por plataforma.
 *
 * Uma contagem de streams por plataforma NÃO responde à pergunta. O Android
 * esteve sete meses sem registar uma única escuta — de 2026-02-10 a 2026-09-11 —
 * e ninguém deu por isso, porque num catálogo em que a faixa mediana chega a um
 * ou dois ouvintes por mês, zero é indistinguível de "hoje ninguém usou
 * Android". Um painel de contagens teria mostrado uma linha sossegada, não uma
 * avaria.
 *
 * O que identifica a falha é uma contradição: a plataforma dá sinais de vida e
 * mesmo assim não regista nada. Os sinais usados aqui — impressões de anúncio e
 * sessões de rádio — não passam pelo bus de eventos do player, que foi a peça
 * que partiu, e por isso continuaram a chegar durante todo o apagão.
 */

const STATUS = {
    ok: { label: "A registar", color: "#10b981", bg: "#ecfdf5", icon: "●" },
    broken: { label: "Sem registar", color: "#b91c1c", bg: "#fef2f2", icon: "▲" },
    idle: { label: "Sem dados", color: "#9ca3af", bg: "#f9fafb", icon: "○" },
};

const PLATFORM_LABEL = { ios: "iOS", android: "Android", web: "Web", desconhecido: "Desconhecido" };

const SOURCE_LABEL = {
    search: "Pesquisa",
    library: "Biblioteca",
    playlist: "Playlist",
    profile: "Perfil",
    discovery: "Descoberta",
    radio: "Rádio",
    web: "Web (legado)",
    other: "Outra",
};

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("pt-PT"));

/** "há 7 meses" diz mais do que uma data — é a leitura que torna um apagão óbvio. */
function ago(ts) {
    if (!ts) return "nunca";
    const then = new Date(ts.replace(" ", "T"));
    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1) return "agora mesmo";
    if (mins < 60) return `há ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `há ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 31) return `há ${days} ${days === 1 ? "dia" : "dias"}`;
    const months = Math.floor(days / 30);
    return `há ${months} ${months === 1 ? "mês" : "meses"}`;
}

function PlatformCard({ row, windowKey }) {
    const w = row.windows[windowKey] || {};
    const st = STATUS[w.status] || STATUS.idle;

    return (
        <div style={{ ...s.statCard, borderLeft: `4px solid ${st.color}`, flexDirection: "column", alignItems: "stretch", gap: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <p style={{ ...s.cardTitle, margin: 0 }}>{PLATFORM_LABEL[row.platform] || row.platform}</p>
                <span style={{ ...s.pill, background: st.bg, color: st.color }}>
                    {st.icon} {st.label}
                </span>
            </div>

            <p style={{ ...s.statValue, color: st.color, margin: "10px 0 0" }}>{fmt(w.streams)}</p>
            <p style={s.statLabel}>streams · {fmt(w.listeners)} ouvintes</p>

            <div style={s.cardFooter}>
                <div>
                    <span style={s.footLabel}>Último stream</span>
                    <span style={s.footValue}>{ago(row.last_stream_at)}</span>
                </div>
                <div>
                    <span style={s.footLabel}>Sinais de vida</span>
                    <span style={s.footValue}>
                        {fmt(w.ad_events)} anúncios · {fmt(w.radio_sessions)} rádio
                    </span>
                </div>
            </div>

            {w.status === "broken" && (
                <p style={s.brokenNote}>
                    A plataforma esteve activa nesta janela e não registou uma única escuta. É esta a
                    forma do apagão do Android.
                </p>
            )}
        </div>
    );
}

export default function StreamHealth() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [windowKey, setWindowKey] = useState("24h");

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data: r } = await izenuAPI.get("/admin/streams/health.php");
            setData(r);
        } catch (e) {
            setError("Não foi possível carregar os dados.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const broken = (data?.platforms || []).filter(
        (p) => (p.windows?.[windowKey] || {}).status === "broken"
    );

    return (
        <div style={s.container}>
            <div style={s.headerRow}>
                <div>
                    <h1 style={s.title}>Saúde dos Streams</h1>
                    <p style={s.subtitle}>
                        Se uma plataforma está viva e não regista escutas, é avaria. Se não há sinal
                        nenhum, é só ausência de dados.
                    </p>
                </div>
                <button style={s.primaryBtn} onClick={fetchData} disabled={loading}>
                    {loading ? "A carregar…" : "Actualizar"}
                </button>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}

            <div style={s.tabs}>
                {["24h", "7d", "30d"].map((k) => (
                    <button
                        key={k}
                        onClick={() => setWindowKey(k)}
                        style={{
                            ...s.chipBtn,
                            ...(windowKey === k ? { background: "#1a1a2e", color: "#fff" } : {}),
                        }}
                    >
                        {k === "24h" ? "Últimas 24h" : k === "7d" ? "7 dias" : "30 dias"}
                    </button>
                ))}
            </div>

            {broken.length > 0 && (
                <div style={s.alertBox}>
                    <strong>
                        {broken.map((p) => PLATFORM_LABEL[p.platform] || p.platform).join(" e ")}
                    </strong>{" "}
                    {broken.length === 1 ? "está activo mas não regista streams" : "estão activos mas não registam streams"}.
                    O último stream {broken.length === 1 ? "foi" : "de cada um foi"}{" "}
                    {broken.map((p) => ago(p.last_stream_at)).join(", ")}.
                </div>
            )}

            {loading && !data ? (
                <p style={s.empty}>A carregar…</p>
            ) : (
                <>
                    <div style={s.row}>
                        {(data?.platforms || [])
                            .filter((p) => p.platform !== "desconhecido" || p.last_stream_at)
                            .map((p) => (
                                <PlatformCard key={p.platform} row={p} windowKey={windowKey} />
                            ))}
                    </div>

                    <div style={s.card}>
                        <h2 style={s.cardTitle}>Origens por plataforma</h2>
                        <p style={s.cardDesc}>
                            Últimos {data?.days || 30} dias. Uma coluna inteira a zero numa plataforma que
                            regista tudo o resto é o segundo tipo de falha — foi assim que se descobriu
                            que a pesquisa do web app nunca chegava, e que a grelha de favoritos
                            reportava <em>Outra</em> em vez de <em>Biblioteca</em>, valendo metade em
                            royalties. Já a coluna <em>Outra</em> ser a maior é normal: é o destino de
                            tudo o que não declara origem, e das faixas despromovidas quando a fila
                            deriva para outro artista.
                        </p>

                        <div style={{ overflowX: "auto" }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Plataforma</th>
                                        {(data?.source_names || []).map((src) => (
                                            <th key={src} style={s.thNum}>{SOURCE_LABEL[src] || src}</th>
                                        ))}
                                        <th style={s.thNum}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.sources || []).map((row) => (
                                        <tr key={row.platform} style={s.tr}>
                                            <td style={s.td}>{PLATFORM_LABEL[row.platform] || row.platform}</td>
                                            {(data?.source_names || []).map((src) => {
                                                const n = row.by_source[src] || 0;
                                                // Um zero só é notável quando a plataforma regista o resto.
                                                const notable = n === 0 && row.total > 0;
                                                return (
                                                    <td
                                                        key={src}
                                                        style={{
                                                            ...s.tdNum,
                                                            ...(notable ? { color: "#b45309", fontWeight: 700 } : {}),
                                                        }}
                                                    >
                                                        {n === 0 ? "0" : fmt(n)}
                                                    </td>
                                                );
                                            })}
                                            <td style={{ ...s.tdNum, fontWeight: 700 }}>{fmt(row.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {(data?.notes || []).map((n, i) => (
                        <p key={i} style={s.hint}>{n}</p>
                    ))}
                    {data?.generated_at && (
                        <p style={s.hint}>Dados de {data.generated_at} (hora do servidor).</p>
                    )}
                </>
            )}
        </div>
    );
}

const s = {
    container: { padding: "0 20px 40px 20px", maxWidth: 1500 },
    headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 },
    title: { fontSize: 24, fontWeight: 700, color: "#1a1a2e", margin: 0 },
    subtitle: { margin: "4px 0 0", fontSize: 13, color: "#999", fontWeight: 400, maxWidth: 640 },
    primaryBtn: { padding: "10px 16px", background: "#10b981", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 14 },
    chipBtn: { padding: "8px 14px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 },
    tabs: { display: "flex", gap: 8, marginBottom: 18 },
    row: { display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" },
    statCard: { flex: "1 1 280px", display: "flex", padding: "18px 20px", backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", minWidth: 280 },
    statLabel: { margin: "2px 0 0", fontSize: 12, color: "#888", fontWeight: 500 },
    statValue: { fontSize: 28, fontWeight: 700, lineHeight: 1.1 },
    pill: { padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" },
    cardFooter: { display: "flex", flexDirection: "column", gap: 6, marginTop: 14, paddingTop: 12, borderTop: "1px solid #f3f4f6" },
    footLabel: { display: "block", fontSize: 10, color: "#aaa", textTransform: "uppercase", fontWeight: 600 },
    footValue: { fontSize: 13, color: "#374151", fontWeight: 500 },
    brokenNote: { margin: "12px 0 0", fontSize: 11, color: "#b91c1c", lineHeight: 1.5 },
    alertBox: { padding: "14px 16px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 8, marginBottom: 18, fontSize: 13, lineHeight: 1.6 },
    errorBox: { padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 8, marginBottom: 16, fontSize: 13 },
    card: { padding: 24, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", marginBottom: 20 },
    cardTitle: { fontSize: 15, fontWeight: 600, color: "#333", margin: "0 0 4px" },
    cardDesc: { margin: "0 0 14px", fontSize: 12, color: "#aaa", fontWeight: 400, lineHeight: 1.6, maxWidth: 860 },
    hint: { margin: "6px 0 0", fontSize: 11, color: "#aaa", lineHeight: 1.4 },
    empty: { color: "#ccc", textAlign: "center", padding: 30, fontSize: 14 },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", textAlign: "left", padding: "8px 6px", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" },
    thNum: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", textAlign: "right", padding: "8px 6px", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" },
    tr: { borderBottom: "1px solid #f5f5f5" },
    td: { padding: "10px 6px", fontSize: 13, color: "#374151", fontWeight: 600 },
    tdNum: { padding: "10px 6px", fontSize: 13, color: "#374151", textAlign: "right", whiteSpace: "nowrap" },
};
